/**
 * Settings UI grouping for feature flags — product-oriented sections, not workstream codes.
 */

import { FEATURE_FLAGS, FLAG_CONFIGS, type FeatureFlagValue } from './featureFlags';
import { WORKSPACE_MODULES } from './workspaceModulesService';

export type FlagSettingsGroupId =
  | 'modules'
  | 'behavior'
  | 'graph'
  | 'guidance'
  | 'previews';

export const FLAG_SETTINGS_GROUPS: Record<
  FlagSettingsGroupId,
  { title: string; description: string }
> = {
  modules: {
    title: 'Platform modules & navigation',
    description:
      'Sidebar entries and module surfaces. Workspace module must also be on for module-linked flags.',
  },
  behavior: {
    title: 'Workflow, sessions & quality',
    description:
      'Behavioral rules and enforcement — cycles, sessions, items, plans, and evidence.',
  },
  graph: {
    title: 'Relationship graph',
    description:
      'Power-ups on Shape → Graph: inspector, impact analysis, cluster resolution, Nexus overlay.',
  },
  guidance: {
    title: 'Guidance & onboarding',
    description: 'Floating Knowledge Navigator overlay and its sub-features.',
  },
  previews: {
    title: 'UI previews & experiments',
    description: 'Swap production pages for design canvases and prototype layouts.',
  },
};

export const FLAG_SETTINGS_GROUP_ORDER: FlagSettingsGroupId[] = [
  'modules',
  'behavior',
  'graph',
  'guidance',
  'previews',
];

const GRAPH_FLAGS = new Set<FeatureFlagValue>([
  FEATURE_FLAGS.GRAPH_INSPECTOR,
  FEATURE_FLAGS.GRAPH_PATH_ANALYSIS,
  FEATURE_FLAGS.GRAPH_CLUSTER_RESOLUTION,
  FEATURE_FLAGS.GRAPH_NEXUS_OVERLAY,
]);

const WIZARD_FLAGS = new Set<FeatureFlagValue>([
  FEATURE_FLAGS.OVERLAY_WIZARD,
  FEATURE_FLAGS.WIZARD_RECOMMENDATIONS,
  FEATURE_FLAGS.WIZARD_TUTORIAL,
  FEATURE_FLAGS.WIZARD_CONTEXTUAL_GUIDANCE,
]);

export function getFlagSettingsGroup(flagValue: FeatureFlagValue): FlagSettingsGroupId {
  if (GRAPH_FLAGS.has(flagValue)) return 'graph';
  if (WIZARD_FLAGS.has(flagValue)) return 'guidance';

  const module = WORKSPACE_MODULES.find((m) => m.flagKey === flagValue);
  if (module?.group === 'preview') return 'previews';

  const config = FLAG_CONFIGS[flagValue];
  if (config?.workstream === 'design') return 'previews';

  if (module) return 'modules';

  return 'behavior';
}
